import { SEO } from "@/components/SEO";
import { PageSchema } from "@/components/seo/PageSchema";
import { LastUpdated } from "@/components/seo/LastUpdated";
import { TopCTA } from "@/components/cta/TopCTA";
import { MidPageCTA } from "@/components/cta/MidPageCTA";
import { BottomCTA } from "@/components/cta/BottomCTA";
import { SymptomChecker } from "@/components/content/SymptomChecker";
import { FixSteps } from "@/components/content/FixSteps";
import { DIYvsDoneForYou } from "@/components/content/DIYvsDoneForYou";
import { FAQSection } from "@/components/content/FAQSection";
import { InternalLinks } from "@/components/content/InternalLinks";
import { LeadFormSection } from "@/components/LeadFormSection";

const LandscapingQuoteFormConversion = () => {
  const symptoms = [
    "Your quote form has less than 3% conversion rate",
    "Visitors start the form but don't complete it",
    "You're getting fewer submissions than website traffic suggests",
    "Form asks detailed project questions upfront",
    "No confirmation or next steps after submission",
    "Form is buried at the bottom of the page"
  ];

  const rootCauses = [
    { cause: "Too many required fields", impact: "Each additional field reduces completions by 5-10%" },
    { cause: "Asking for project details too early", impact: "Visitors don't know specifics before consultation" },
    { cause: "No progress indicator for multi-step forms", impact: "Visitors don't know how much longer it takes" },
    { cause: "Form placement below the fold", impact: "Visitors who don't scroll never see it" },
    { cause: "No trust signals near the form", impact: "Visitors hesitate to share contact info" },
    { cause: "Weak or vague call-to-action", impact: "'Submit' doesn't motivate action" },
    { cause: "No confirmation page or email", impact: "Visitors unsure if submission worked" },
    { cause: "Mobile form usability issues", impact: "Fields too small, keyboard problems, slow loading" }
  ];

  const fixSteps = [
    { step: "Reduce to 5 essential fields maximum", details: "Name, phone, email, project type dropdown, optional notes—that's it" },
    { step: "Add form above the fold on key pages", details: "Place a quote form or prominent CTA visible without scrolling" },
    { step: "Show trust signals adjacent to the form", details: "Display reviews, certifications, or guarantees right next to the form" },
    { step: "Use action-oriented button text", details: "'Get My Free Estimate' converts better than 'Submit'" },
    { step: "Create a compelling thank-you page", details: "Confirm submission, set expectations for response time, offer next steps" },
    { step: "Send immediate confirmation email", details: "Auto-email confirms receipt and provides your contact info" },
    { step: "Optimize for mobile completion", details: "Large tap targets, correct keyboard types, minimal scrolling" }
  ];

  const faqs = [
    { question: "What's a good conversion rate for landscaping quote forms?", answer: "3-8% is typical for well-optimized landscaping quote forms. If your form converts under 2%, there are likely usability or trust issues. Above 8% usually indicates excellent form design combined with high-quality traffic." },
    { question: "How many fields should my landscaping quote form have?", answer: "4-5 fields maximum for initial contact: name, phone, email, project type (dropdown), and optional message. Save detailed questions for the consultation—getting the initial lead is what matters." },
    { question: "Should I ask for project budget on the quote form?", answer: "No. Budget questions scare away leads and are rarely answered honestly. Determine budget during the consultation when you can provide context about what their investment includes." },
    { question: "Where should I place my quote form on the page?", answer: "Multiple locations: above the fold on your homepage (or a prominent CTA to it), at the end of service pages, and as a floating element or sticky bar on mobile. Make it impossible to miss." },
    { question: "What should my form button say?", answer: "Use action-oriented, benefit-focused text: 'Get My Free Estimate,' 'Request a Consultation,' or 'Get Started Today.' Avoid generic 'Submit' or 'Send' which don't motivate action." },
    { question: "Do I need a multi-step form for landscaping?", answer: "Only if you have many necessary questions. Single-page forms with 4-5 fields convert best. If you must use steps, show a progress indicator and keep it to 2-3 steps maximum." },
    { question: "Should I require email or phone number?", answer: "Require both, but make it clear why. Phone allows for quick follow-up; email provides written confirmation. Position it as 'We'll call to discuss your project and email a summary.'" },
    { question: "What happens after someone submits a form?", answer: "Best practice: show a thank-you page (not just a message), send an immediate confirmation email, and call within 1 hour during business hours. Speed of response dramatically affects conversion to actual customers." },
    { question: "How do I know my form is the problem?", answer: "Check your analytics for form abandonment rate. If visitors start but don't complete the form, there's a usability issue. If few visitors even start the form, it's a placement or visibility issue." },
    { question: "Should I use a pop-up form?", answer: "Exit-intent pop-ups can recover leaving visitors, but avoid aggressive pop-ups that appear immediately—they frustrate users and increase bounce rate. A sticky bar or floating button is less intrusive." }
  ];

  const links = [
    { to: "/websites/", label: "Website Subscription Pricing" },
    { to: "/landscaping-website-design/", label: "Landscaping Website Design" },
    { to: "/website-templates/lawn-care-quote-form-template/", label: "Quote Form Template" },
    { to: "/website-fixes/landscaping-website-not-getting-leads/", label: "Website Not Getting Leads" }
  ];

  const CANONICAL = "https://www.leafrushmarketing.com/website-fixes/landscaping-quote-form-conversion/";

  return (
    <>
      <SEO
        title="Landscaping Quote Form Low Conversion? Fix It"
        description="Is your landscaping quote form not converting visitors into leads? Learn optimization strategies to dramatically increase submissions."
        canonical={CANONICAL}
        ogUrl={CANONICAL}
      />
      <PageSchema
        url={CANONICAL}
        title="Landscaping Quote Form Low Conversion? Fix It"
        description="Is your landscaping quote form not converting visitors into leads? Learn optimization strategies."
        faqs={faqs}
      />
      <LastUpdated />

      <article className="py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          <header className="mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Landscaping Quote Form Low Conversion? Here's How to Fix It
            </h1>
            <p className="text-xl text-muted-foreground">
              Visitors are coming to your site, but your quote form isn't turning them into leads. Let's change that.
            </p>
          </header>

          <TopCTA />

          <SymptomChecker symptoms={symptoms} title="Signs Your Form Is Underperforming" />

          <section className="my-12">
            <h2 className="text-3xl font-bold text-foreground mb-6">Root Causes: Why Forms Fail to Convert</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {rootCauses.map((item, index) => (
                <div key={index} className="p-4 bg-muted rounded-lg">
                  <h3 className="font-semibold text-foreground">{item.cause}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{item.impact}</p>
                </div>
              ))}
            </div>
          </section>

          <MidPageCTA />

          <FixSteps steps={fixSteps} title="7 Steps to Optimize Your Quote Form" />

          <section className="my-12">
            <h2 className="text-3xl font-bold text-foreground mb-6">The Form Friction Problem</h2>
            <div className="prose prose-lg max-w-none text-foreground">
              <p>
                Every question you ask on your form is a hurdle. Every second of hesitation is an opportunity to lose the lead. <strong>The best-converting forms are the ones that require the least effort from visitors.</strong>
              </p>
              <p>
                For landscaping, visitors often don't know project specifics yet—they want to talk to someone. Asking for detailed requirements, measurements, or budgets before a consultation creates friction that kills conversions.
              </p>
              <p>
                At <a href="/websites/" className="text-primary hover:underline">Leaf Rush Marketing</a>, we design quote forms that capture leads with minimum friction. Our forms use proven conversion principles to maximize submissions while collecting everything you need for initial contact.
              </p>
            </div>
          </section>

          <DIYvsDoneForYou />

          <FAQSection faqs={faqs} />

          <InternalLinks links={links} title="Related Resources" />

          <BottomCTA />

          <LeadFormSection title="Get Your Form Optimized" />
        </div>
      </article>
    </>
  );
};

export default LandscapingQuoteFormConversion;
